import { makeAutoObservable, runInAction } from 'mobx'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { showToast } from 'shared/ui'
import { logger } from 'shared/lib'
import { ZIndex } from 'shared/constants/zIndex'
import { DropdownCardsStore } from 'entities/Billing/ui/DropdownCards'
import { type CreditPrice } from 'entities/Billing/model/CreditPrice'
import { type IParamsUpdateBillingCredits } from 'entities/Billing/api/types'
import { AutoRechargeWidgetStore } from 'entities/Billing/ui/AutoRechargeModal'
import { BuyCreditsModalActions } from 'entities/Billing/ui/BuyCreditsModal/ui/BuyCreditsModalActions'
import { BuyCreditsModalContent } from 'entities/Billing/ui/BuyCreditsModal/ui/BuyCreditsModalContent'
import type { BillingStore } from 'entities/Billing'
import type { SubscriptionStore } from 'entities/Subscription'
import type { OrganizationStore } from 'entities/Organization'

export class BuyCreditsModalStore {
  private _zIndex = ZIndex.OVERLAY_TOP_100
  private _idModal = 'buy_credits_modal'
  private _activeCreditPrice: CreditPrice | null = null
  private _dropdownCardsStore: DropdownCardsStore
  private _autoRechargeWidgetStore: AutoRechargeWidgetStore
  private _loading = false
  private _showAutoRechargeCard = false

  constructor(
    private _subscriptionStore: SubscriptionStore,
    private _organizationStore: OrganizationStore,
    private _billingStore: BillingStore
  ) {
    this._dropdownCardsStore = new DropdownCardsStore(this._billingStore, {
      enableAch: true,
    })
    this._autoRechargeWidgetStore = new AutoRechargeWidgetStore(this._billingStore, {
      zIndex: this._zIndex,
    })

    makeAutoObservable(this)
  }

  reset = () => {
    this._activeCreditPrice = null
    this._showAutoRechargeCard = false
  }

  openModal = () => {
    this._showAutoRechargeCard = !this._autoRechargeWidgetStore.isActive

    modalStore.addModal({
      id: this._idModal,
      title: 'Buy credits',
      width: 480,
      ModalActions: BuyCreditsModalActions,
      ModalContent: BuyCreditsModalContent,
      ModalContentProps: {
        buyCreditsModalStore: this,
      },
      paddingContent: '0 22px 14px 24px',
      onClose: this.closeModal,
      zIndex: this._zIndex,
    })
  }

  closeModal = () => {
    this.reset()
    modalStore.removeModal(this._idModal)
  }

  handleActivePrice = (item: CreditPrice) => {
    this._activeCreditPrice = item
  }

  handleBuyCredits = async () => {
    if (this.disabled) return
    if (!this.activeSource) return
    if (!this._activeCreditPrice) return

    try {
      runInAction(() => {
        this._loading = true
      })

      const params: IParamsUpdateBillingCredits = {
        payment_method: this.activeSource.id,
        quantity: this._activeCreditPrice.credits,
      }

      const response = await this._billingStore.handleBuyCredits(params)

      if (response) {
        showToast({
          title: `${this._activeCreditPrice.creditsLabel} credits purchased`,
          type: 'success',
        })

        this._organizationStore.fetchOrganization()

        this.closeModal()
      } else {
        this._dropdownCardsStore.setPaymentError(
          'We were unable to process your payment. Please select a different card'
        )
      }
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  get activeSource() {
    return this._dropdownCardsStore?.activeItem
  }

  get isOutOfMessage() {
    const trialCredits = this._organizationStore.trialCredits
    const accountCredits = this._organizationStore.accountCredits
    const isTrial = this._subscriptionStore.isTrial

    if (isTrial && trialCredits <= 0) {
      return true
    }

    if (!isTrial && accountCredits <= 0) {
      if (this._billingStore.autorecharge?.is_active) return false
      return true
    }

    return false
  }

  get disabled() {
    return (
      this.loadingModal ||
      this._billingStore.buyCreditsLoading ||
      !this.activeSource ||
      !this._activeCreditPrice
    )
  }

  get loadingModal() {
    return this._billingStore.sourcesLoading || this._billingStore.creditsPriceLoading
  }

  get textSave() {
    if (!this._activeCreditPrice) return 'Save'

    return `Buy ${this._activeCreditPrice.creditsLabel} credits for $${this._activeCreditPrice.priceLabel}`
  }

  get dropdownCardsStore() {
    return this._dropdownCardsStore
  }

  get autoRechargeWidgetStore() {
    return this._autoRechargeWidgetStore
  }

  get zIndex() {
    return this._zIndex
  }

  get loading() {
    return this._loading
  }

  get showAutoRechargeCard() {
    return this._showAutoRechargeCard
  }
}
